/**
 * Iter-236 — Campaign Intelligence Phase 2
 *
 * Client Insights Roll-Up Dashboard
 *   Route: /client/insights
 *
 * Aggregates every shift+recap for the current client across a date
 * window, exposes range toggle (30d/90d/YTD/Custom), best-performing
 * events list (click → /client/events/:eventKey/insights), the
 * Executive KPI strip, AI recommendations, AI opportunities, the
 * Benchmark Section, and the Quarterly PDF history.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Sparkles, TrendingUp, TrendingDown, Calendar,
  Award, FileDown, Loader2, RefreshCw, ExternalLink, Mail,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  AreaChart, Area,
} from 'recharts';
import CreatorAttributionPanel from '../../components/insights/CreatorAttributionPanel';
import BonusManager from '../../components/insights/BonusManager';
import GoalHitFeed from '../../components/insights/GoalHitFeed';

const fmt = (n) => (n || 0).toLocaleString();
const money = (cents) =>
  cents ? `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '$0';

const Delta = ({ pct, testid }) => {
  if (pct === null || pct === undefined) return <span className="text-slate-400 text-xs">—</span>;
  const up = pct >= 0;
  return (
    <span
      className={`inline-flex items-center text-xs font-semibold ${up ? 'text-emerald-600' : 'text-rose-600'}`}
      data-testid={testid}
    >
      {up ? <TrendingUp className="w-3 h-3 mr-0.5" /> : <TrendingDown className="w-3 h-3 mr-0.5" />}
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
};

const KPI = ({ label, value, sub, testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid={testid}>
    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">{value}</div>
    {sub != null && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
  </div>
);

const SectionHeader = ({ icon: Icon, title, subtitle, action }) => (
  <div className="flex items-start justify-between mb-3 mt-8 flex-wrap gap-2">
    <div className="flex items-start gap-3">
      {Icon && <Icon className="w-5 h-5 text-cyan-500 mt-1" />}
      <div>
        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {action}
  </div>
);

const RANGE_LABELS = { '30d': 'Last 30 days', '90d': 'Last 90 days', ytd: 'Year to date', custom: 'Custom' };

function currentQuarter() {
  const d = new Date();
  return `${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`;
}

function previousQuarter(q) {
  const [yr, qn] = q.split('-Q').map(Number);
  if (qn === 1) return `${yr - 1}-Q4`;
  return `${yr}-Q${qn - 1}`;
}

export default function ClientInsightsRollup({ asAdmin, clientIdOverride }) {
  const navigate = useNavigate();
  const [clientId, setClientId] = useState(clientIdOverride || null);
  const [range, setRange] = useState('90d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenLoading, setRegenLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [emailModal, setEmailModal] = useState(null);   // { quarter }
  const [distLists, setDistLists] = useState([]);
  const [sendMode, setSendMode] = useState('list');     // 'seats' | 'list' | 'both' | 'adhoc'
  const [chosenListId, setChosenListId] = useState('');
  const [adhocEmails, setAdhocEmails] = useState('');

  // For non-admin: pull the current user id
  useEffect(() => {
    if (clientId) return;
    api.get('/auth/me').then((r) => setClientId(r.data?.user_id || r.data?.id)).catch(() => {});
  }, [clientId]);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const res = await api.get(`/insights/client/${clientId}`, { params: { range } });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load insights');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [clientId, range]);

  const loadHistory = useCallback(async () => {
    if (!clientId) return;
    try {
      const r = await api.get(`/insights/quarterly/${clientId}/history`);
      setHistory(r.data?.reports || []);
    } catch (e) { /* silent */ }
  }, [clientId]);

  const loadDistLists = useCallback(async () => {
    if (!clientId) return;
    try {
      const r = await api.get(`/insights/distribution-lists?client_id=${clientId}`);
      const ls = r.data?.lists || [];
      setDistLists(ls);
      const def = ls.find((l) => l.is_default);
      if (def) setChosenListId(def.id);
      else if (ls[0]) setChosenListId(ls[0].id);
    } catch (e) { /* silent */ }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { loadDistLists(); }, [loadDistLists]);

  const regenerate = useCallback(async () => {
    setRegenLoading(true);
    try {
      await api.post(`/insights/client/${clientId}/regenerate-ai`, null, { params: { range } });
      toast.success('AI insights refreshed');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI regeneration failed');
    } finally {
      setRegenLoading(false);
    }
  }, [clientId, range, load]);

  const generatePDF = useCallback(async (quarter) => {
    setPdfLoading(true);
    try {
      const r = await api.post(`/insights/quarterly/${clientId}/generate`, null,
        { params: { quarter } });
      toast.success(`Quarterly PDF generated · score ${r.data?.performance_score}/100`);
      await loadHistory();
      // Iter-262c — open the streaming endpoint, NOT the raw storage path
      // (the raw path was hitting the React 404 page in production).
      const url = `${api.defaults.baseURL}/insights/quarterly/${clientId}/download/${quarter}/pdf`;
      // Attach token for the new tab — open via blob to support auth header
      try {
        const pdfRes = await api.get(`/insights/quarterly/${clientId}/download/${quarter}/pdf`,
          { responseType: 'blob' });
        const blobUrl = URL.createObjectURL(new Blob([pdfRes.data], { type: 'application/pdf' }));
        window.open(blobUrl, '_blank');
      } catch (openErr) {
        window.open(url, '_blank');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'PDF generation failed');
    } finally {
      setPdfLoading(false);
    }
  }, [clientId, loadHistory]);

  const openPDF = useCallback(async (quarter) => {
    // Iter-262c — used by History table "View" buttons
    try {
      const pdfRes = await api.get(`/insights/quarterly/${clientId}/download/${quarter}/pdf`,
        { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([pdfRes.data], { type: 'application/pdf' }));
      window.open(blobUrl, '_blank');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not open PDF');
    }
  }, [clientId]);

  const emailPDF = useCallback(async (quarter) => {
    setEmailModal({ quarter });
  }, []);

  const sendEmailNow = useCallback(async () => {
    if (!emailModal) return;
    const payload = {
      include_seats: sendMode === 'seats' || sendMode === 'both',
      list_id: (sendMode === 'list' || sendMode === 'both') ? chosenListId : null,
      ad_hoc_emails: sendMode === 'adhoc'
        ? adhocEmails.split(/[,\s]+/).map((e) => e.trim()).filter(Boolean)
        : [],
    };
    try {
      const r = await api.post(`/insights/quarterly/${clientId}/email`,
        payload, { params: { quarter: emailModal.quarter } });
      if (r.data?.ok) {
        toast.success(`Sent to ${r.data.recipients?.length || 0} recipient(s)`);
      } else {
        toast.error(r.data?.reason || 'Email failed');
      }
      setEmailModal(null);
      await loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Email failed');
    }
  }, [emailModal, sendMode, chosenListId, adhocEmails, clientId, loadHistory]);

  if (loading || !clientId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8">
        <p>Insights unavailable.</p>
        <Button onClick={() => navigate(-1)} className="mt-4">Back</Button>
      </div>
    );
  }

  const exec = data.executive_kpi || {};
  const perf = data.campaign_performance || {};
  const bench = data.benchmarks || {};
  const ai = data.ai_insights || {};
  const curQ = currentQuarter();
  const prevQ = previousQuarter(curQ);

  return (
    <div className="min-h-screen bg-slate-50 pb-20" data-testid="client-insights-rollup">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-6 py-6">
          {!asAdmin && (
            <button onClick={() => navigate('/client/home')}
              className="text-cyan-300 text-sm flex items-center mb-3 hover:opacity-80"
              data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Home
            </button>
          )}
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-cyan-300/70">
                Campaign Intelligence · Rollup
              </div>
              <h1 className="text-2xl md:text-4xl font-bold mt-1">
                {RANGE_LABELS[range]}{' '}
                <span className="text-cyan-300 text-base">{data.range?.start} → {data.range?.end}</span>
              </h1>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {['30d','90d','ytd'].map((r) => (
                <button key={r}
                  onClick={() => setRange(r)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                    range === r ? 'bg-cyan-300 text-slate-900' : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                  data-testid={`range-${r}`}>
                  {RANGE_LABELS[r]}
                </button>
              ))}
              <Button variant="ghost"
                className="text-cyan-300 border border-cyan-300/30 hover:bg-cyan-300/10"
                onClick={regenerate} disabled={regenLoading}
                data-testid="regenerate-ai-btn">
                {regenLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Refresh AI
              </Button>
              <Button className="bg-cyan-300 text-slate-900 hover:bg-cyan-400"
                onClick={() => generatePDF(prevQ)} disabled={pdfLoading}
                data-testid="generate-pdf-btn">
                {pdfLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                Quarterly PDF
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        {/* Executive KPI Strip */}
        <SectionHeader icon={Award} title="Executive Summary"
          subtitle="Cross-campaign performance" />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <KPI label="Activations" value={fmt(exec.events)} testid="exec-events" />
          <KPI label="Staff" value={fmt(exec.staff)} testid="exec-staff" />
          <KPI label="Engagements" value={fmt(exec.engagements)} testid="exec-engagements" />
          <KPI label="Leads" value={fmt(exec.leads)} testid="exec-leads" />
          <KPI label="Samples" value={fmt(exec.samples)} testid="exec-samples" />
          <KPI label="Sentiment" value={exec.sentiment_score != null ? `${exec.sentiment_score}/100` : '—'}
            testid="exec-sentiment" />
          <KPI label="Performance" value={`${exec.performance_score}/100`}
            sub="Composite score" testid="exec-perf" />
        </div>

        {/* Engagements trend chart */}
        {data.daily_trend?.length > 1 && (
          <>
            <SectionHeader title="Engagement & Sample Trend" />
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={data.daily_trend}>
                  <defs>
                    <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0DEAE3" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#0DEAE3" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Area type="monotone" dataKey="engagements" stroke="#0DEAE3" fill="url(#g1)" strokeWidth={2} />
                  <Area type="monotone" dataKey="samples" stroke="#6366F1" fill="transparent" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </>
        )}

        {/* Best Performing Events */}
        <SectionHeader title="Best Performing Activations" />
        {data.best_events?.length === 0 && (
          <p className="text-sm text-slate-500">No completed activations with metrics in this window.</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="best-events">
          {(data.best_events || []).map((e, i) => (
            <button key={e.event_key + i}
              onClick={() => navigate(`/client/events/${encodeURIComponent(e.event_key)}/insights`)}
              className="bg-white border border-slate-200 hover:border-cyan-300 hover:shadow rounded-xl p-4 text-left transition"
              data-testid={`best-event-${i}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold text-slate-900">{e.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {e.city}, {e.state} · {e.date}
                  </div>
                </div>
                <ExternalLink className="w-4 h-4 text-slate-400" />
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                <div>
                  <div className="text-sm font-bold text-slate-900">{fmt(e.engagements)}</div>
                  <div className="text-[10px] text-slate-500 uppercase">Engage</div>
                </div>
                <div>
                  <div className="text-sm font-bold text-slate-900">{fmt(e.samples)}</div>
                  <div className="text-[10px] text-slate-500 uppercase">Samples</div>
                </div>
                <div>
                  <div className="text-sm font-bold text-slate-900">{fmt(e.leads)}</div>
                  <div className="text-[10px] text-slate-500 uppercase">Leads</div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* AI Insights */}
        {ai?.summary ? (
          <>
            <SectionHeader icon={Sparkles} title="AI Insights" />
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <p className="text-slate-800 leading-relaxed italic">{ai.summary}</p>
              <Badge className="bg-cyan-100 text-cyan-900 mt-2">
                Sentiment: {ai.sentiment_label || '—'}
                {ai.sentiment_score ? ` (${ai.sentiment_score}/100)` : ''}
              </Badge>
            </div>
          </>
        ) : null}

        {/* AI Recommendations — always rendered for predictable testids */}
        <SectionHeader icon={Sparkles} title="AI Recommendations"
          subtitle="Staffing, scheduling, and product messaging guidance" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <RecCard label="Staffing" items={ai?.recommendations?.staffing} testid="rec-staffing" />
          <RecCard label="Scheduling" items={ai?.recommendations?.scheduling} testid="rec-scheduling" />
          <RecCard label="Product Messaging" items={ai?.recommendations?.messaging} testid="rec-messaging" />
        </div>

        {ai?.opportunities?.length > 0 && (
          <>
            <SectionHeader title="AI Opportunities" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {ai.opportunities.map((op, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-xl p-4"
                  data-testid={`opportunity-${i}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="font-semibold text-slate-900">{op.title}</div>
                    <Badge className={
                      op.impact === 'high'
                        ? 'bg-rose-100 text-rose-900'
                        : op.impact === 'low' ? 'bg-slate-100 text-slate-700'
                        : 'bg-amber-100 text-amber-900'
                    }>{(op.impact || 'medium').toUpperCase()}</Badge>
                  </div>
                  <p className="text-sm text-slate-600">{op.detail}</p>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Iter-237 — Creator Attribution */}
        <CreatorAttributionPanel
          attribution={data.attribution}
          objective={data.attribution?.revenue_by_objective?.[0]?.objective}
        />

        {/* Iter-238 — Creator Pay-for-Performance */}
        <BonusManager clientId={clientId} />

        {/* Iter-239 — Goal Hit celebration feed */}
        <GoalHitFeed clientId={clientId} />

        {/* Benchmarks */}
        <SectionHeader title="Benchmarks"
          subtitle="vs prior quarter · vs year ago · vs network · vs similar campaign type" />
        <BenchmarkTable bench={bench} />

        {/* Quarterly History */}
        {history.length > 0 && (
          <>
            <SectionHeader icon={Calendar} title="Quarterly PDF History" />
            <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100"
              data-testid="quarterly-history">
              {history.map((h) => (
                <div key={h.id} className="flex flex-wrap items-center justify-between p-4 gap-3">
                  <div>
                    <div className="font-semibold text-slate-900">{h.quarter}</div>
                    <div className="text-xs text-slate-500">
                      {h.range_start} → {h.range_end} · Score {h.performance_score ?? '—'}/100
                      {h.emailed_at && <> · emailed {new Date(h.emailed_at).toLocaleDateString()}</>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {h.pdf_url && (
                      <button type="button"
                        onClick={() => openPDF(h.quarter)}
                        data-testid={`download-${h.quarter}`}
                        className="px-3 py-1.5 rounded bg-slate-100 hover:bg-slate-200 text-xs font-semibold text-slate-700 inline-flex items-center">
                        <FileDown className="w-3.5 h-3.5 mr-1" /> Download
                      </button>
                    )}
                    <Button size="sm" variant="outline"
                      onClick={() => emailPDF(h.quarter)} data-testid={`email-${h.quarter}`}>
                      <Mail className="w-3.5 h-3.5 mr-1" /> Email
                    </Button>
                    <Button size="sm" variant="outline"
                      onClick={() => generatePDF(h.quarter)} data-testid={`regen-${h.quarter}`}>
                      Regenerate
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Iter-237 — Send-to-Team email picker */}
      {emailModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4"
          data-testid="email-modal" onClick={(e) => e.target === e.currentTarget && setEmailModal(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-xl">
            <h3 className="text-lg font-bold text-slate-900">
              Send {emailModal.quarter} PDF
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              Choose who receives the report. Distribution-list recipients do not consume platform seats.
            </p>
            <div className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-slate-50"
                data-testid="send-mode-seats">
                <input type="radio" name="sendmode" checked={sendMode === 'seats'}
                  onChange={() => setSendMode('seats')} className="mt-1" />
                <div>
                  <div className="text-sm font-medium">Seated team members only</div>
                  <div className="text-xs text-slate-500">Primary owner + invited team within your seat limit.</div>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-slate-50"
                data-testid="send-mode-list">
                <input type="radio" name="sendmode" checked={sendMode === 'list'}
                  onChange={() => setSendMode('list')} className="mt-1" />
                <div className="flex-1">
                  <div className="text-sm font-medium">Distribution list</div>
                  <select value={chosenListId} onChange={(e) => setChosenListId(e.target.value)}
                    className="mt-1 border border-slate-200 rounded px-2 py-1 text-sm w-full"
                    data-testid="dist-list-select">
                    {distLists.length === 0 && <option>(create one in Settings)</option>}
                    {distLists.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name} ({(l.recipients || []).length}) {l.is_default ? '★ default' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-slate-50"
                data-testid="send-mode-both">
                <input type="radio" name="sendmode" checked={sendMode === 'both'}
                  onChange={() => setSendMode('both')} className="mt-1" />
                <div>
                  <div className="text-sm font-medium">Both — seats + distribution list</div>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-slate-50"
                data-testid="send-mode-adhoc">
                <input type="radio" name="sendmode" checked={sendMode === 'adhoc'}
                  onChange={() => setSendMode('adhoc')} className="mt-1" />
                <div className="flex-1">
                  <div className="text-sm font-medium">Ad-hoc emails (one time)</div>
                  <textarea value={adhocEmails} onChange={(e) => setAdhocEmails(e.target.value)}
                    placeholder="vp@brand.com, ceo@brand.com"
                    rows={2}
                    className="mt-1 border border-slate-200 rounded px-2 py-1 text-sm w-full"
                    data-testid="adhoc-emails-input" />
                </div>
              </label>
            </div>
            <div className="flex items-center justify-between mt-4">
              <button onClick={() => navigate('/client/settings/distribution-lists')}
                className="text-xs text-cyan-700 hover:underline"
                data-testid="manage-lists-link">
                Manage distribution lists →
              </button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEmailModal(null)}>Cancel</Button>
                <Button onClick={sendEmailNow} className="bg-cyan-300 text-slate-900 hover:bg-cyan-400"
                  data-testid="send-email-btn">Send</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RecCard({ label, items, testid }) {
  return (
    <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-xl p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-cyan-300 mb-2">{label}</div>
      {(!items || items.length === 0) && <p className="text-sm text-slate-300">No recommendations.</p>}
      <ul className="space-y-2">
        {(items || []).slice(0, 5).map((it, i) => (
          <li key={i} className="text-sm leading-snug">
            <span className="text-cyan-300 mr-1">►</span>{it}
          </li>
        ))}
      </ul>
    </div>
  );
}

function BenchmarkTable({ bench }) {
  const rows = [
    { key: 'consumer_engagements', label: 'Engagements', isMoney: false },
    { key: 'samples_distributed',  label: 'Samples',     isMoney: false },
    { key: 'leads_collected',      label: 'Leads',       isMoney: false },
    { key: 'qr_scans',             label: 'QR Scans',    isMoney: false },
    { key: 'retail_sales_cents',   label: 'Retail Sales', isMoney: true },
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="benchmark-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left px-4 py-2.5">Metric</th>
            <th className="text-right px-3 py-2.5">Current</th>
            <th className="text-right px-3 py-2.5">vs Prior Q</th>
            <th className="text-right px-3 py-2.5">vs YoY</th>
            <th className="text-right px-3 py-2.5">vs Network</th>
            <th className="text-right px-3 py-2.5">vs Similar</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const item = bench[r.key] || {};
            const cur = r.isMoney
              ? `$${((item.current || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
              : (item.current || 0).toLocaleString();
            return (
              <tr key={r.key} className="border-t border-slate-100" data-testid={`bench-${r.key}`}>
                <td className="px-4 py-2 text-slate-900 font-medium">{r.label}</td>
                <td className="px-3 py-2 text-right text-slate-900">{cur}</td>
                <td className="px-3 py-2 text-right"><Delta pct={item.vs_prior_quarter_pct} testid={`bench-${r.key}-pq`} /></td>
                <td className="px-3 py-2 text-right"><Delta pct={item.vs_year_ago_pct} testid={`bench-${r.key}-yoy`} /></td>
                <td className="px-3 py-2 text-right"><Delta pct={item.vs_network_pct} testid={`bench-${r.key}-net`} /></td>
                <td className="px-3 py-2 text-right"><Delta pct={item.vs_similar_pct} testid={`bench-${r.key}-sim`} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-4 py-2 text-xs text-slate-500 border-t border-slate-100 bg-slate-50">
        Network benchmark: {bench.network_clients || 0} comparable client(s) in this window.
      </div>
    </div>
  );
}
